import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ArrowUpRight, FileDown, FileText } from "lucide-react";
import { Chapter } from "../../lib/motion";
import { CATEGORIES, PRODUCTS } from "../../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Products({ onInquire }) {
  const [active, setActive] = useState("All");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    return PRODUCTS.filter((p) => {
      const catOk = active === "All" || p.category === active;
      const q = query.trim().toLowerCase();
      const qOk = !q || p.name.toLowerCase().includes(q) || p.description.toLowerCase().includes(q);
      return catOk && qOk;
    });
  }, [active, query]);

  return (
    <section id="products" data-testid="products" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Chapter number="02" overline="Our Range" title="Product categories built for food service." />

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-12">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setActive(c)}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                  active === c ? "bg-verra-blue text-white" : "bg-verra-grey text-verra-blue/70 hover:bg-verra-light/30"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 w-full lg:w-auto">
            <div className="relative flex-1 lg:w-64">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-verra-blue/40" />
              <input
                data-testid="product-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search products…"
                className="w-full rounded-full border border-verra-blue/10 bg-verra-base pl-11 pr-4 py-3 text-sm text-verra-blue outline-none focus:border-verra-green transition-colors"
              />
            </div>
            <a
              href={`${API}/catalog.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-catalog-btn"
              className="shrink-0 inline-flex items-center gap-2 rounded-full bg-verra-green text-white text-sm font-semibold px-5 py-3 hover:bg-verra-blue transition-colors duration-300"
            >
              <FileDown className="w-4 h-4" /> Catalog
            </a>
          </div>
        </div>

        <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {filtered.map((p) => (
              <motion.article
                key={p.id}
                layout
                data-testid={`product-card-${p.id}`}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="group rounded-2xl bg-verra-grey overflow-hidden border border-transparent hover:border-verra-green/20 transition-colors duration-300"
              >
                <div className="overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <span className="overline">{p.category}</span>
                  <h3 className="font-display text-2xl text-verra-blue mt-2">{p.name}</h3>
                  <p className="text-sm text-verra-blue/60 mt-2 leading-relaxed">{p.description}</p>

                  <div className="mt-4 space-y-2 text-sm">
                    <p className="text-verra-blue/70"><span className="font-semibold text-verra-blue">Sizes:</span> {p.sizes.join(", ")}</p>
                    <p className="text-verra-blue/70"><span className="font-semibold text-verra-blue">Material:</span> {p.material}</p>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {p.features.map((f) => (
                      <span key={f} className="text-[11px] rounded-full bg-verra-light/25 text-verra-green px-2.5 py-1 font-medium">{f}</span>
                    ))}
                  </div>

                  <div className="mt-6 flex items-center gap-2">
                    <button
                      data-testid={`inquire-${p.id}`}
                      onClick={() => onInquire(p.name)}
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-verra-blue text-white text-sm font-semibold py-3 hover:bg-verra-green transition-colors duration-300"
                    >
                      Inquire Now <ArrowUpRight className="w-4 h-4" />
                    </button>
                    <a
                      href={`${API}/products/${p.id}/spec.pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`spec-${p.id}`}
                      title="Download spec sheet (PDF)"
                      aria-label={`Download ${p.name} spec sheet`}
                      className="shrink-0 inline-flex items-center justify-center rounded-full border border-verra-blue/20 text-verra-blue w-11 h-11 hover:bg-verra-green hover:text-white hover:border-verra-green transition-colors duration-300"
                    >
                      <FileText className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>

        {filtered.length === 0 && (
          <p data-testid="no-products" className="text-center text-verra-blue/50 py-16">No products match your search.</p>
        )}
      </div>
    </section>
  );
}
